import { useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

export default function TermsPage() {
  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      document.documentElement.style.setProperty('--header-nav-height', `${headerH + navH}px`)
    }
    measure()
    requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return (
    <>
      <div className="bg-gray-50 min-h-screen text-[#232323]">
        <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
          <ContentColumn anchor="terms-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
                Terms &amp; Conditions
              </h1>
              <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-10">
                The terms that govern your use of gazar.dev.
              </p>
            </div>
          </ContentColumn>
        </section>

        <section className="border-b border-border-subtle bg-white">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-label font-body text-gray-900/60">
                <li>
                  <Link to="/" className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none">Home</Link>
                </li>
                <li className="font-heading text-gray-900">/</li>
                <li className="font-heading text-gray-900" aria-current="page">Terms &amp; Conditions</li>
              </ol>
            </nav>
          </div>
        </section>

        <section className="bg-white py-20">
          <ContentColumn anchor="terms-main" className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg max-w-none">
              <div className="bg-white border-2 border-gray-200 rounded-smooth-lg p-8 sm:p-12 shadow-sm">
                <h2 className="font-heading text-3xl font-bold uppercase text-gray-900 mb-6">Terms &amp; Conditions</h2>

                <div className="space-y-6 font-body text-base text-[#232323] leading-relaxed">
                  <div>
                    <p className="mb-4"><strong>Last Updated:</strong> July 2026</p>
                    <p className="mb-4">
                      These Terms &amp; Conditions ("Terms") govern your access to and use of gazar.dev (the "Site"), a brand and portfolio operated by QUEEK LIMITED ("we", "our", or "us"). By creating an account, purchasing a course, or otherwise using the Site, you agree to these Terms. If you do not agree, please do not use the Site.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Accounts</h3>
                    <p className="mb-4">
                      You may create an account to access certain features and purchased content. You are responsible for keeping your login credentials secure and for all activity under your account. You must provide accurate information and be able to enter a binding contract. We may suspend or terminate accounts that violate these Terms.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Courses &amp; Content</h3>
                    <p className="mb-4">
                      Some courses are offered free of charge; others require a one-time payment. Free lessons and previews are provided for evaluation. Purchasing a course grants you a personal, non-exclusive, non-transferable licence to access that course's content for your own learning. You may not resell, redistribute, publicly share, or reproduce the content without our written permission. All content remains the intellectual property of QUEEK LIMITED or its licensors.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Payments</h3>
                    <p className="mb-4">
                      Paid courses are sold at the price shown at checkout. Payments are processed securely by Stripe; we do not store your card details. Access to purchased content is granted once payment is confirmed. Prices and course availability may change, but changes will not affect content you have already purchased.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Refunds</h3>
                    <p className="mb-4">
                      If you are not satisfied with a paid course, contact us within 14 days of purchase at{' '}
                      <a href="mailto:me@gazar.dev" className="text-[#2d5016] hover:text-[#1a5a3a] hover:underline font-semibold">me@gazar.dev</a>{' '}
                      and we will consider a refund in line with your statutory rights.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Acceptable Use</h3>
                    <p className="mb-4">
                      You agree not to misuse the Site, attempt to gain unauthorised access, circumvent access controls or paywalls, scrape content at scale, or use the Site in any way that is unlawful or harms others.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Disclaimer &amp; Liability</h3>
                    <p className="mb-4">
                      The Site and its content are provided "as is" for educational purposes and do not constitute professional advice. To the fullest extent permitted by law, we exclude liability for any indirect or consequential loss arising from your use of the Site. Nothing in these Terms limits liability that cannot be limited by law.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Changes to These Terms</h3>
                    <p className="mb-4">
                      We may update these Terms from time to time. Material changes will be posted on this page with an updated "Last Updated" date. Continued use of the Site after changes means you accept the revised Terms.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">Contact</h3>
                    <p className="mb-4">
                      Questions about these Terms? Contact us at{' '}
                      <a href="mailto:me@gazar.dev" className="text-[#2d5016] hover:text-[#1a5a3a] hover:underline font-semibold">me@gazar.dev</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ContentColumn>
        </section>
      </div>
    </>
  )
}
